import { useCallback, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { roundPricing } from "@dune/utils";
import {
	useCreatePricingDetailsMutation,
	useUpdatePricingDetailsMutation,
} from "../../../api/mutations/pricing-details.mutations";
import { usePricingDetailsQuery } from "../../../api/queries/pricing-details.queries";
import {
	doPricingFieldKeys,
	mrtPricingFieldKeys,
	trcPricingFieldKeys,
} from "@dune/vars";
import type {
	CreatePricingDetailsInput,
	OffersWarrantyType,
	PricingDetails,
	PricingDetailsFormValues,
} from "@dune/types";
import { parsePriceToNumber } from "../../../components/inputs/PriceInput";
import { parseRateStringToFraction } from "../../../components/inputs/RateInput";

const baseSchema = {
	duneManagementFeesHT: z.string().optional(),
	duneManagementFeesTaxes: z.string().optional(),
	duneManagementFeesTTC: z.string().optional(),
	intermediaryManagementFeesHT: z.string().optional(),
	intermediaryManagementFeesTaxes: z.string().optional(),
	intermediaryManagementFeesTTC: z.string().optional(),
	brokerCommission: z.string().optional(),
	brokerRemuneration: z.string().optional(),
	totalClientPrimeHT: z.string().optional(),
	totalClientPrimeTaxes: z.string().optional(),
	totalClientPrimeTTC: z.string().optional(),
	agreedRateHT: z.string().optional(),
	agreedRateTTC: z.string().optional(),
};

function objectFromKeys(keys: readonly string[]): Record<string, z.ZodTypeAny> {
	const shape: Record<string, z.ZodTypeAny> = {};
	for (const k of keys) {
		shape[k] = z.string().optional();
	}
	return shape;
}

export const zDoSchema = z.object({
	...baseSchema,
	...objectFromKeys(doPricingFieldKeys),
});

export const zMrtSchema = z.object({
	...baseSchema,
	...objectFromKeys(mrtPricingFieldKeys),
});

export const zTrcSchema = z.object({
	...baseSchema,
	...objectFromKeys(trcPricingFieldKeys),
});

export const usePricingDetailsForm = (params: {
	pricingDetailsId?: number;
	warrantyType: OffersWarrantyType;
	onSuccess?: () => void;
}) => {
	const { pricingDetailsId, warrantyType, onSuccess } = params;
	const mode = pricingDetailsId ? "update" : "create";
	const schema = useMemo(
		() =>
			warrantyType === "DO"
				? zDoSchema
				: warrantyType === "MRT"
					? zMrtSchema
					: zTrcSchema,
		[warrantyType],
	);
	const form = useForm<PricingDetailsFormValues>({
		resolver: zodResolver(schema),
		defaultValues: {},
	});

	const createMutation = useCreatePricingDetailsMutation();
	const updateMutation = useUpdatePricingDetailsMutation();
	const { data: fetchedDetails } = usePricingDetailsQuery(pricingDetailsId);

	const mapPricingDetailsToFormValues = useCallback(
		(details: PricingDetails): PricingDetailsFormValues => {
			const base: PricingDetailsFormValues = {
				duneManagementFeesHT: roundPricing(
					details.duneManagementFeesHT ?? null,
				),
				duneManagementFeesTaxes: roundPricing(
					details.duneManagementFeesTaxes ?? null,
				),
				duneManagementFeesTTC: roundPricing(
					details.duneManagementFeesTTC ?? null,
				),
				intermediaryManagementFeesHT: roundPricing(
					details.intermediaryManagementFeesHT ?? null,
				),
				intermediaryManagementFeesTaxes: roundPricing(
					details.intermediaryManagementFeesTaxes ?? null,
				),
				intermediaryManagementFeesTTC: roundPricing(
					details.intermediaryManagementFeesTTC ?? null,
				),
				brokerCommission: roundPricing(details.brokerCommission ?? null),
				brokerRemuneration: roundPricing(details.brokerRemuneration ?? null),
				totalClientPrimeHT: roundPricing(details.totalClientPrimeHT ?? null),
				totalClientPrimeTaxes: roundPricing(
					details.totalClientPrimeTaxes ?? null,
				),
				totalClientPrimeTTC: roundPricing(details.totalClientPrimeTTC ?? null),
				agreedRateHT:
					details.agreedRateHT != null
						? roundPricing(details.agreedRateHT * 100)
						: "",
				agreedRateTTC:
					details.agreedRateTTC != null
						? roundPricing(details.agreedRateTTC * 100)
						: "",
			};

			if (warrantyType === "DO" && details.doDetails) {
				const extra: Record<string, string> = {};
				for (const key of doPricingFieldKeys)
					extra[key] = roundPricing(details.doDetails[key] ?? null);
				return { ...base, ...extra };
			}
			if (warrantyType === "MRT" && details.mrtDetails) {
				const extra: Record<string, string> = {};
				for (const key of mrtPricingFieldKeys)
					extra[key] = roundPricing(details.mrtDetails[key] ?? null);
				return { ...base, ...extra };
			}
			if (warrantyType === "TRC" && details.trcDetails) {
				const extra: Record<string, string> = {};
				for (const key of trcPricingFieldKeys)
					extra[key] = roundPricing(details.trcDetails[key] ?? null);
				return { ...base, ...extra };
			}
			return base;
		},
		[warrantyType],
	);

	const { reset } = form;

	useEffect(() => {
		if (!fetchedDetails) return;
		reset(mapPricingDetailsToFormValues(fetchedDetails));
	}, [fetchedDetails, reset, mapPricingDetailsToFormValues]);

	const toPayload = useCallback(
		(values: PricingDetailsFormValues) =>
			pricingDetailsFormValuesToCreatePayload(values, warrantyType),
		[warrantyType],
	);

	const onSubmit = useCallback(
		async (values: PricingDetailsFormValues) => {
			const payload = toPayload(values);
			if (mode === "create") await createMutation.mutateAsync(payload);
			else {
				if (pricingDetailsId == null) {
					console.warn("No pricing details id provided for update");
					return;
				}
				await updateMutation.mutateAsync({ id: pricingDetailsId, ...payload });
			}
			onSuccess?.();
		},
		[
			createMutation,
			updateMutation,
			mode,
			pricingDetailsId,
			onSuccess,
			toPayload,
		],
	);

	return { form, onSubmit } as const;
};

export const pricingDetailsFormValuesToCreatePayload = (
	values: PricingDetailsFormValues,
	warrantyType: OffersWarrantyType,
): CreatePricingDetailsInput => {
	const payload: CreatePricingDetailsInput = {
		duneManagementFeesHT: parsePriceToNumber(values.duneManagementFeesHT),
		duneManagementFeesTaxes: parsePriceToNumber(values.duneManagementFeesTaxes),
		duneManagementFeesTTC: parsePriceToNumber(values.duneManagementFeesTTC),
		intermediaryManagementFeesHT: parsePriceToNumber(
			values.intermediaryManagementFeesHT,
		),
		intermediaryManagementFeesTaxes: parsePriceToNumber(
			values.intermediaryManagementFeesTaxes,
		),
		intermediaryManagementFeesTTC: parsePriceToNumber(
			values.intermediaryManagementFeesTTC,
		),
		totalClientPrimeHT: parsePriceToNumber(values.totalClientPrimeHT),
		totalClientPrimeTaxes: parsePriceToNumber(values.totalClientPrimeTaxes),
		totalClientPrimeTTC: parsePriceToNumber(values.totalClientPrimeTTC),
		agreedRateHT: parseRateStringToFraction(values.agreedRateHT),
		agreedRateTTC: parseRateStringToFraction(values.agreedRateTTC),
		brokerCommission: parsePriceToNumber(values.brokerCommission),
		brokerRemuneration: parsePriceToNumber(values.brokerRemuneration),
	};

	if (warrantyType === "DO") {
		const extra: Partial<
			Record<(typeof doPricingFieldKeys)[number], number | null>
		> = {};
		for (const key of doPricingFieldKeys)
			extra[key] = parsePriceToNumber(values[key]);
		payload.doDetails = extra;
	}
	if (warrantyType === "MRT") {
		const extra: Partial<
			Record<(typeof mrtPricingFieldKeys)[number], number | null>
		> = {};
		for (const key of mrtPricingFieldKeys)
			extra[key] = parsePriceToNumber(values[key]);
		payload.mrtDetails = extra;
	}
	if (warrantyType === "TRC") {
		const extra: Partial<
			Record<(typeof trcPricingFieldKeys)[number], number | null>
		> = {};
		for (const key of trcPricingFieldKeys)
			extra[key] = parsePriceToNumber(values[key]);
		payload.trcDetails = extra;
	}
	return payload;
};
